import React, { useEffect, useState } from 'react';
import { Modal, Select, Checkbox } from 'antd';
import FloatLabel from '../../../components/FloatLabel';
import { Controller, Input, useForm, yupResolver, SubmitHandler, useGetApiRequests, useSelector, ErrorMessages, ErrorMessage } from '../../../common/CommonExport';
import { CreateDepartmentSchema } from './CreateDepartmentSchema';
import { DepartmentApiProps, DepartmentProps } from '../OrganisationsInterfaces';
import ModalLoader from '../../../components/Loader/ModalLoader';
import PopupModal from '../../employee/employee-details/employee-profile/modal/PopupModal';
import { AxiosError, AxiosResponse } from 'axios';
import { HodItem, HodOption, DepartmentRootState, CreateDepartmentPayload } from './types/DepartmentTypes';

const CreateDepartmentModal: React.FC<DepartmentApiProps> = ({ departmentApi, setModalOpen, modalOpen, editRecord, editModal }: DepartmentApiProps) => {
    const getHodListApi = useGetApiRequests('hodList', 'GET');
    const createDepartmentApi = useGetApiRequests('createDepartment', 'POST');
    const editDepartmentApi = useGetApiRequests('editDepartment', 'PUT');
    const orgId = useSelector((state: DepartmentRootState) => state?.organisationId?.id);
    const [hodList, setHodList] = useState<HodOption[]>([]);
    const [propHodId, setPropHodId] = useState<string | undefined>(editRecord?.hodId);
    const [errorHod, seterrorHod] = useState<boolean>(true);
    const [modalEdit, setModalEdit] = useState(editModal);
    const [createHod, setCreateHod] = useState<string | undefined>();
    const [loader, setLoader] = useState(false)
    const [modalerrMessage, setModalErrMessage] = useState('')
    const [popModal, setPopModal] = useState(false)
    const [modalResponse, setModalResponse] = useState('')
    const [modalButtonShow, setmodalButtonShow] = useState(true)

    useEffect(() => {
        setLoader(true)
        const params: { start: number; limit: number; organisationId: string; isActive: boolean } = {
            start: 0,
            limit: 100000,
            organisationId: orgId,
            isActive: true,
        };
        getHodListApi('', params)
            .then((response: AxiosResponse | AxiosError) => {
                const res = response as AxiosResponse;
                const reformedHodList = res?.data?.data.map((item: HodItem) => ({
                    value: item.employeeId,
                    email: item.officialEmail,
                    label: item.firstName + ' ' + item.lastName,
                    employeeId: item.employeeId,
                }));
                setLoader(false)
                setHodList(reformedHodList);
            })
            .catch((err: AxiosError) => {
                // triggerNotification('error', '', err?.message, 'topRight');
                setPopModal(true)
                setmodalButtonShow(false)
                setModalErrMessage(err?.message)
                setModalResponse('error')
                setTimeout(() => {
                    handleClosePopUpModal()
                    setLoader(false);
                }, 1500)
            });
        if (!modalEdit) {
            reset({ email: '', description: '', departmentName: '', hod: '', isRosterPlanEligible: false });
        } else if (modalEdit) {
            if (editRecord !== undefined && editRecord !== null && Object.keys(editRecord).length > 0) {
                setValue('departmentName', editRecord?.department || '');
                setValue('description', editRecord?.description || '');
                setValue('email', editRecord?.email || '');
                setValue('hod', editRecord?.hodId || '');
                setValue('isRosterPlanEligible', editRecord?.isRosterPlanEligible);
            }
        }
    }, []);
    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { errors },
    } = useForm<DepartmentProps>({ resolver: yupResolver(CreateDepartmentSchema) });
    const onChange = (value: string) => {
        if (modalEdit) {
            const hodTempListEdit = hodList.find((item: HodOption) => item.value === value);
            setValue('hod', value);
            setPropHodId(value);
            seterrorHod(false);
            setValue('email', hodTempListEdit?.email || '');

        } else {
            const hodTempList = hodList.find((item: HodOption) => item.value === value);
            setValue('hod', value);
            setPropHodId(value);
            setCreateHod(hodTempList?.value)
            seterrorHod(false);
            setValue('email', hodTempList?.email || '');
        }
    };
    const handleClose = () => {
        reset({ email: '', description: '', departmentName: '', hod: '', isRosterPlanEligible: false });
        setModalOpen(false);
        setModalEdit(false);
    };
    const handleApiCall = async (payload: CreateDepartmentPayload, pathParams: Record<string, string | number>, successMessage: string) => {
        try {
            await (modalEdit ? editDepartmentApi(payload, {}, pathParams) : createDepartmentApi(payload));
            setPopModal(true)
            setmodalButtonShow(false)
            setModalErrMessage(successMessage)
            setModalResponse('success')
            setTimeout(() => {
                handleClosePopUpModal()
                setLoader(false);
            }, 1500)
            departmentApi();
        } catch (err: unknown) {
            const error = err as AxiosError<{ message: string }>;
            // triggerNotification('error', '', err?.response?.data?.message, 'topRight');
            setPopModal(true)
            setmodalButtonShow(false)
            setModalErrMessage(error?.response?.data?.message || 'Something went wrong')
            setModalResponse('error')
            setTimeout(() => {
                handleClosePopUpModal()
                setLoader(false);
            }, 1500)
        }
    };
    const filterOption = (input: string, option?: { label: string; value: string }) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
    const onSubmit: SubmitHandler<DepartmentProps> = (data: DepartmentProps) => {
        const payload: CreateDepartmentPayload = {
            description: data?.description,
            leadEmail: data?.email,
            name: data?.departmentName,
            organisationId: orgId,
            headOfDepartment: {
                employeeId: data?.hod,
            },
            isRosterPlanEligible: data?.isRosterPlanEligible,
        };

        if (modalEdit && editRecord) {
            const pathParams: Record<string, string | number> = {
                id: editRecord.key,
            };
            const editPayload: CreateDepartmentPayload = {
                ...payload,
                departmentId: editRecord.key,
            };
            handleApiCall(editPayload, pathParams, ErrorMessages.departmentUpdation);
        } else {
            handleApiCall(payload, {}, ErrorMessages.departmentCreation);
        }
    };

    const handleClosePopUpModal = () => {
        setPopModal(false)
        setModalOpen(false);
    }

    return (
        <Modal data-testid="modalEl" title={`${modalEdit ? 'Edit' : 'Create'} Department`} centered open={modalOpen} onCancel={handleClose} footer={null} maskClosable={false}>
            <div className='relative z-0 mt-5 '>
                <Controller name='departmentName' control={control} render={({ field }) => <Input type='text' id='departmentName' label='Department Name*' control={control} {...field} hasError={!!errors.departmentName} />} />
                {errors.departmentName && <ErrorMessage message={errors.departmentName.message} />}
            </div>
            <div className='relative z-0 mt-5 lg:-ml-1'>
                <FloatLabel label='Select HOD*' name='hod' value={modalEdit ? propHodId : createHod}>
                    <Select className='float-control' showSearch filterOption={filterOption} options={hodList} value={modalEdit ? propHodId : createHod} onChange={onChange} />
                </FloatLabel>
                {errors.hod && errorHod && <ErrorMessage message={errors.hod.message?.toString()} />}
            </div>

            <div className='relative z-0 mt-5 pointer-events-none'>
                <Controller name='email' control={control} render={({ field }) => <Input type='email' id='email' label='Department Email ID*' control={control} {...field} hasError={!!errors.email} />} />
                {errors.email && <ErrorMessage message={errors.email.message} />}
            </div>

            <div className='relative z-0 mt-5 '>
                <Controller name='description' control={control} render={({ field }) => <Input type='text' id='description' label='Description*' control={control} {...field} hasError={!!errors.description} />} />
                {errors.description && <ErrorMessage message={errors.description.message} />}
            </div>
            <div className='mt-5'>
                <Controller
                    name='isRosterPlanEligible'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Checkbox checked={value} onChange={onChange}>
                            Allow Roster Plan
                        </Checkbox>
                    )}
                />
            </div>
            <div className='mt-5 flex justify-end '>
                <button className='addButton mr-4 secondary' onClick={handleClose}  >
                    Cancel
                </button>
                <button className='addButton mr-4 ' onClick={handleSubmit(onSubmit)}  >
                    {modalEdit ? 'Update' : 'Create'}
                </button>
            </div>
            {popModal && <PopupModal message={modalerrMessage} open={popModal} handleModalClose={handleClosePopUpModal} response={modalResponse} modalButtonShow={modalButtonShow} />}

            {loader && <ModalLoader />}
        </Modal>

    );
};

export default CreateDepartmentModal;
